import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Calendar, Clock, ArrowRight, TrendingUp, Mail } from 'lucide-react';
import { Button } from '../components/ui/Button';
type Post = {
  title: string;
  category: string;
  date: string;
  readTime: string;
  excerpt: string;
  img: string;
  author: string;
  trending?: boolean;
};
const posts: Post[] = [{
  title: "The 2026 Hiring Outlook: Where the Jobs Are (and Where They Aren't)",
  category: 'Industry Trends',
  date: 'May 12, 2026',
  readTime: '8 min read',
  excerpt: 'Our annual outlook breaks down which industries are accelerating hiring in Q2 2026, which are contracting, and what it means for your next move.',
  img: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1400&q=80',
  author: 'Glenn M. Rossi',
  trending: true
}, {
  title: 'How AI Resume Screeners Have Changed in 2026 — And How to Beat Them',
  category: 'Resume Tips',
  date: 'May 8, 2026',
  readTime: '7 min read',
  excerpt: "ATS systems have evolved beyond simple keyword matching. Here's what our executive resume team has learned writing for the new generation of AI screeners.",
  img: 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
  author: 'Shawn Tate',
  trending: true
}, {
  title: 'Why Executive Search Is Booming Again in 2026',
  category: 'Executive Search',
  date: 'May 3, 2026',
  readTime: '6 min read',
  excerpt: 'After a slowdown in late 2025, C-suite hiring has surged. We unpack the four forces behind the resurgence and what executives should be doing right now.',
  img: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
  author: 'Glenn M. Rossi'
}, {
  title: "LinkedIn's New Algorithm: What Recruiters Actually See in 2026",
  category: 'LinkedIn Tips',
  date: 'Apr 28, 2026',
  readTime: '5 min read',
  excerpt: 'We analyzed 500+ recruiter searches in the last 90 days. The patterns are clear — and most candidates are getting filtered out before they ever know it.',
  img: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
  author: 'Juliette Langford',
  trending: true
}, {
  title: "The Career Transitioner's Playbook: Pivoting Industries After 40",
  category: 'Career Growth',
  date: 'Apr 22, 2026',
  readTime: '9 min read',
  excerpt: 'Three real client stories on how seasoned professionals successfully repositioned themselves into entirely new sectors — and what they all did right.',
  img: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
  author: 'Olivia Grace Thompson'
}, {
  title: 'Remote, Hybrid, or RTO? The 2026 Workplace Reality Check',
  category: 'Job Search',
  date: 'Apr 15, 2026',
  readTime: '6 min read',
  excerpt: "The pendulum has swung again. Here's what our placement data reveals about where employers really stand on remote work — and how to negotiate it.",
  img: 'https://images.unsplash.com/photo-1593642632823-8f78536788c6?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
  author: 'Juliette Langford'
}, {
  title: 'Salary Negotiation in a Cooling Market: New Rules for 2026',
  category: 'Career Growth',
  date: 'Apr 8, 2026',
  readTime: '7 min read',
  excerpt: 'The leverage has shifted, but you still have more power than you think. Five updated tactics our placement team uses to secure offers above-band.',
  img: 'https://images.unsplash.com/photo-1554224154-22dec7ec8818?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
  author: 'Shawn Tate'
}, {
  title: 'Top Industries Hiring Talent Sourcers Right Now',
  category: 'Industry Trends',
  date: 'Apr 1, 2026',
  readTime: '5 min read',
  excerpt: 'From climate tech to AI infrastructure, here are the seven sectors where our recruitment team has placed the most candidates in the past quarter.',
  img: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
  author: 'Glenn M. Rossi'
}];
const categories = ['All', 'Industry Trends', 'Executive Search', 'Resume Tips', 'LinkedIn Tips', 'Career Growth', 'Job Search'];
// Map category → accent color for editorial variety
const categoryColors: Record<string, {
  bg: string;
  text: string;
  dot: string;
}> = {
  'Industry Trends': {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    dot: 'bg-blue-600'
  },
  'Executive Search': {
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    dot: 'bg-purple-600'
  },
  'Resume Tips': {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    dot: 'bg-emerald-600'
  },
  'LinkedIn Tips': {
    bg: 'bg-sky-50',
    text: 'text-sky-700',
    dot: 'bg-sky-600'
  },
  'Career Growth': {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    dot: 'bg-amber-600'
  },
  'Job Search': {
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    dot: 'bg-rose-600'
  }
};
const getCatColor = (cat: string) => categoryColors[cat] || {
  bg: 'bg-gray-100',
  text: 'text-gray-700',
  dot: 'bg-gray-500'
};
const authorProfiles: Record<string, {
  name: string;
  title: string;
  credential: string;
}> = {
  'Glenn M. Rossi': {
    name: 'Wills Martins',
    title: 'Founder & Chief Executive Officer',
    credential: '25+ years in executive search and leadership advisory'
  },
  'Shawn Tate': {
    name: 'Jason Gregory',
    title: 'Executive Resume Writer & Documentation Specialist',
    credential: 'Executive branding and career-positioning specialist'
  },
  'Juliette Langford': {
    name: 'Juliette Langford',
    title: 'Talent Source Specialist',
    credential: 'Technology and financial-services talent sourcing'
  },
  'Olivia Grace Thompson': {
    name: 'Olivia Grace Thompson',
    title: 'TalentForge Consulting',
    credential: 'Healthcare and life-sciences leadership advisory'
  }
};
function getAuthorProfile(author: string) {
  return authorProfiles[author] || {
    name: author,
    title: 'TalentBridge Consultant',
    credential: 'Executive search and talent advisory'
  };
}
export function BlogPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const filtered = posts.filter(p => {
    const matchesCategory = activeCategory === 'All' || p.category === activeCategory;
    const matchesSearch = searchQuery === '' || p.title.toLowerCase().includes(searchQuery.toLowerCase()) || p.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  const [featured, ...rest] = filtered;
  return <div className="pt-20 bg-white" data-id="element-311" data-mp="1ftq2g6rtck6i">
      {/* Hero — editorial masthead */}
      <section className="relative bg-navy-900 text-white overflow-hidden" data-id="element-312" data-mp="1frvcge1oq1vk">
        {/* Decorative accents */}
        <div className="absolute top-1/2 left-0 w-96 h-96 bg-gold-500/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl" data-id="element-313" data-mp="1pjsrxwzq7g03" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-navy-700/30 rounded-full translate-x-1/3 translate-y-1/3 blur-3xl" data-id="element-314" data-mp="1kssumqmp2vx2" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative" data-id="element-315" data-mp="1yiwfbd5xehke">
          <div className="max-w-3xl" data-id="element-316" data-mp="1tkl2ctkc3qxy">
            <div className="flex items-center gap-3 mb-6" data-id="element-317" data-mp="1tnqs8b0sewuq">
              <div className="w-12 h-px bg-gold-500" data-id="element-318" data-mp="28hc5ndx4pmsu" />
              <span className="text-gold-500 font-semibold tracking-[0.25em] uppercase text-xs" data-id="element-319" data-mp="23jkb0c53nmky">
                The TalentBridge Journal
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-serif font-bold mb-6 leading-[1.1]" data-id="element-320" data-mp="e6qdb7h6t0ik">
              Career intelligence,
              <br data-id="element-321" data-mp="3ji189ckjyd5b" />
              <span className="text-gold-500 italic" data-id="element-322" data-mp="3jijen20xi7qy">delivered weekly.</span>
            </h1>
            <p className="text-lg text-gray-300 mb-10 max-w-xl leading-relaxed" data-id="element-323" data-mp="3ogb29jujj7pc">
              In-the-trenches insights from our recruiters, placement
              specialists, and executive search team — based on what's actually
              working for clients right now.
            </p>

            {/* Search */}
            <div className="relative max-w-xl" data-id="element-324" data-mp="30jx99aydpuh8">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-325" data-mp="30l8cn9nwx07t" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search articles, topics, authors..." className="w-full pl-14 pr-6 py-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold-500 focus:bg-white/15 transition-all" data-id="element-326" data-mp="35jte1cewhpxu" />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-50 border-b border-gray-200" data-id="element-327" data-mp="2a8d5ydd75867">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-7 flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-10" data-id="element-328" data-mp="2ab91xw5hhms0">
          <div className="text-sm font-semibold text-navy-900 whitespace-nowrap" data-id="element-329" data-mp="20jlhif7ch601">Executive Search Intelligence</div>
          <p className="text-sm text-gray-700 leading-relaxed" data-id="element-330" data-mp="1vk8sbnt03mua">
            Practical insight from our retained executive search, leadership advisory, and executive career-positioning practice—not high-volume staffing advice.
          </p>
          <div className="flex flex-wrap gap-2 text-xs font-semibold text-navy-900" data-id="element-331" data-mp="1mjkdwrzp3kgv">
            {['Retained Executive Search', 'Board & C-Suite', 'Leadership Advisory'].map(scope => <span key={scope} className="border border-gold-500/40 bg-white px-3 py-1.5" data-id="element-332" data-mp="3gwk2ham1zp0a">{scope}</span>)}
          </div>
        </div>
      </section>

      {/* Categories — pill nav */}
      <section className="sticky top-20 z-30 bg-white/90 backdrop-blur-md border-b border-gray-200" data-id="element-333" data-mp="103ly5v85pu1s">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 overflow-x-auto" data-id="element-334" data-mp="v62lgzr3xpby">
          <div className="flex items-center gap-2 min-w-max" data-id="element-335" data-mp="195lfypqzx25s">
            <span className="text-xs uppercase tracking-widest text-gray-400 font-bold mr-4 hidden md:inline" data-id="element-336" data-mp="146io67010sfr">
              Topics
            </span>
            {categories.map(cat => {
            const isActive = activeCategory === cat;
            return <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${isActive ? 'bg-navy-900 text-white shadow-md' : 'bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-navy-900'}`} data-id="element-337" data-mp="ce1efa46isrg">
                  {cat}
                </button>;
          })}
          </div>
        </div>
      </section>

      {/* Featured Article */}
      {featured && <section className="py-16 bg-white" data-id="element-338" data-mp="295xcdysz6etb">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-339" data-mp="296qnfbyyvqh9">
            <div className="flex items-center gap-3 mb-8" data-id="element-340" data-mp="1vf2aj6gw8xz8">
              <TrendingUp className="h-5 w-5 text-gold-500" data-id="element-341" data-mp="1vcft8f47b43t" />
              <span className="text-xs uppercase tracking-[0.25em] font-bold text-navy-900" data-id="element-342" data-mp="20eonrfwq2e81">
                Featured This Week
              </span>
              <div className="flex-1 h-px bg-gray-200" data-id="element-343" data-mp="2o1dcolk5f6y5" />
            </div>

            <motion.article initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center group cursor-pointer" data-id="element-344" data-mp="2s4062qwgwzxg">
              {/* Image */}
              <div className="relative overflow-hidden rounded-2xl aspect-[4/3] shadow-2xl" data-id="element-345" data-mp="nmlinloob0a0">
                <img src={featured.img} alt={featured.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-346" data-mp="jl8gpmopbh8d" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/40 to-transparent" data-id="element-347" data-mp="9w89jg01pfj3" />
                <div className="absolute top-5 left-5 flex items-center gap-2" data-id="element-348" data-mp="4xexls0epwl1">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 ${getCatColor(featured.category).bg} ${getCatColor(featured.category).text} text-xs font-bold uppercase tracking-wider rounded-full shadow-md`} data-id="element-349" data-mp="3w25acg9p4amy">
                    <span className={`w-1.5 h-1.5 rounded-full ${getCatColor(featured.category).dot}`} data-id="element-350" data-mp="3n41kx8vlatpv" />
                    {featured.category}
                  </span>
                  {featured.trending && <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-gold-500 text-white text-xs font-bold uppercase tracking-wider rounded-full shadow-md" data-id="element-351" data-mp="1st3bl4dmdxyf">
                      <TrendingUp className="h-3 w-3" data-id="element-352" data-mp="22k677qns5kqb" />
                      Trending
                    </span>}
                </div>
              </div>

              {/* Content */}
              <div data-id="element-353" data-mp="19vsade8fgugc">
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-5" data-id="element-354" data-mp="19waesy58uezc">
                  <span className="flex items-center gap-1.5" data-id="element-355" data-mp="1etk2a892ogkj">
                    <Calendar className="h-4 w-4" data-id="element-356" data-mp="1ew6isxdxy6ew" /> {featured.date}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-gray-300" data-id="element-357" data-mp="1jvj43dp5z5fd" />
                  <span className="flex items-center gap-1.5" data-id="element-358" data-mp="1nwl807u105no">
                    <Clock className="h-4 w-4" data-id="element-359" data-mp="2v94ut3ao9f30" /> {featured.readTime}
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-navy-900 mb-6 leading-tight group-hover:text-gold-600 transition-colors" data-id="element-360" data-mp="2z95jdgrhj6ns">
                  {featured.title}
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-8" data-id="element-361" data-mp="2svhrsfq5s7i8">
                  {featured.excerpt}
                </p>
                <div className="flex items-end justify-between gap-6" data-id="element-362" data-mp="37out3sy7bw67">
                  <div className="flex items-start gap-3" data-id="element-363" data-mp="32qjkfljn7rpk">
                    <div className="w-11 h-11 rounded-full bg-navy-900 text-white flex items-center justify-center font-bold text-sm flex-shrink-0" data-id="element-364" data-mp="3grdv6au5y4re">
                      {getAuthorProfile(featured.author).name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <div data-id="element-365" data-mp="3cpt4mahcqmj">
                      <div className="font-bold text-navy-900 text-sm" data-id="element-366" data-mp="3e0wil00jwd4">{getAuthorProfile(featured.author).name}</div>
                      <div className="text-xs text-gray-600" data-id="element-367" data-mp="3vicxj44enx2j">{getAuthorProfile(featured.author).title}</div>
                      <div className="text-xs text-gray-500 mt-1" data-id="element-368" data-mp="i0hhkqihuw67">{getAuthorProfile(featured.author).credential}</div>
                    </div>
                  </div>
                  <span className="hidden sm:inline-flex items-center gap-2 text-navy-900 font-semibold text-sm" data-id="element-369" data-mp="8zt6a3khj9x0">
                    Expert analysis <ArrowRight className="h-4 w-4" data-id="element-370" data-mp="dw9raowe185v" />
                  </span>
                </div>
              </div>
            </motion.article>
          </div>
        </section>}

      {/* Article Grid */}
      <section className="py-20 bg-gray-50" data-id="element-371" data-mp="42003aj6mfwq">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-372" data-mp="3mepfd3dm0z55">
          <div className="flex items-end justify-between mb-12" data-id="element-373" data-mp="3mc3ajbi3sxqm">
            <div data-id="element-374" data-mp="3qg15da0ebmdv">
              <h2 className="text-2xl md:text-3xl font-serif font-bold text-navy-900" data-id="element-375" data-mp="3qdf39pw9xz31">
                {activeCategory === 'All' ? 'Latest Stories' : activeCategory}
              </h2>
              <p className="text-gray-500 mt-1 text-sm" data-id="element-376" data-mp="3he1oqitamkbg">
                {rest.length} article{rest.length === 1 ? '' : 's'} · Updated
                weekly
              </p>
            </div>
          </div>

          {rest.length === 0 ? <div className="bg-white rounded-2xl p-16 text-center border border-gray-100" data-id="element-377" data-mp="1e61by6fruejf">
              <p className="text-gray-500" data-id="element-378" data-mp="197gci98cfyw1">No articles match your search.</p>
            </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7" data-id="element-379" data-mp="25xz2lja8fjs4">
              {rest.map((post, index) => {
            const cat = getCatColor(post.category);
            return <motion.article key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.05
            }} className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-gray-200 hover:shadow-xl transition-all duration-300 flex flex-col group" data-id="element-380" data-mp="1wvzlys34qhpp" data-mpi={index}>
                    <div className="relative h-52 overflow-hidden" data-id="element-381" data-mp="l40aceutqez8">
                      <img src={post.img} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-382" data-mp="l5beifs6loqv" />
                      <div className="absolute bottom-4 left-4" data-id="element-383" data-mp="z4b1zk3yjxlg">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 ${cat.bg} ${cat.text} text-[10px] font-bold uppercase tracking-wider rounded-full shadow-sm`} data-id="element-384" data-mp="1422t4bws3fol">
                          <span className={`w-1.5 h-1.5 rounded-full ${cat.dot}`} data-id="element-385" data-mp="35uqxwpp2mb6h" />
                          {post.category}
                        </span>
                      </div>
                    </div>

                    <div className="p-6 flex-1 flex flex-col" data-id="element-386" data-mp="1lbf4guudw2o4">
                      <div className="flex items-center gap-3 text-xs text-gray-500 mb-3" data-id="element-387" data-mp="1lavk77h9h26x">
                        <span className="flex items-center gap-1" data-id="element-388" data-mp="1gd3rib8slc1o">
                          <Calendar className="h-3 w-3" data-id="element-389" data-mp="sm7xd7gv64r9" /> {post.date}
                        </span>
                        <span className="w-1 h-1 rounded-full bg-gray-300" data-id="element-390" data-mp="nopsbs5yw9oj" />
                        <span className="flex items-center gap-1" data-id="element-391" data-mp="11nfuvnbk31ka">
                          <Clock className="h-3 w-3" data-id="element-392" data-mp="11ll7818wgvci" /> {post.readTime}
                        </span>
                      </div>

                      <h3 className="text-xl font-serif font-bold text-navy-900 mb-3 leading-snug line-clamp-2 group-hover:text-gold-600 transition-colors" data-id="element-393" data-mp="2d2kyqunfb65h">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-5 line-clamp-3 flex-1 leading-relaxed" data-id="element-394" data-mp="2gkin6vo037so">
                        {post.excerpt}
                      </p>

                      <div className="flex items-start gap-2 pt-4 border-t border-gray-100" data-id="element-395" data-mp="26mm67n8dtimn">
                        <div className="w-7 h-7 rounded-full bg-navy-900 text-white flex items-center justify-center font-bold text-[10px] flex-shrink-0" data-id="element-396" data-mp="2blpav6kg9mu3">
                          {getAuthorProfile(post.author).name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                        <div data-id="element-397" data-mp="2uka0eu6p0k5m">
                          <div className="text-xs font-semibold text-navy-900" data-id="element-398" data-mp="2uji4fi96oket">{getAuthorProfile(post.author).name}</div>
                          <div className="text-[11px] text-gray-600 leading-snug" data-id="element-399" data-mp="3i8kqd2auozf4">{getAuthorProfile(post.author).title}</div>
                        </div>
                      </div>
                    </div>
                  </motion.article>;
          })}
            </div>}
        </div>
      </section>

      <section className="py-12 bg-white border-t border-gray-200" data-id="element-400" data-mp="1nhgr33xnaodg">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-401" data-mp="18q80ari0awyq">
          <div className="border border-gray-200 bg-gray-50 p-7 md:p-8" data-id="element-402" data-mp="1dp2ipsc2ajib">
            <div className="flex items-start gap-4" data-id="element-403" data-mp="zqb8wi48fjzz">
              <div className="w-9 h-9 rounded-full bg-gold-500/15 flex items-center justify-center text-gold-600 font-serif font-bold flex-shrink-0" data-id="element-404" data-mp="zoi292tmczrz">i</div>
              <div data-id="element-405" data-mp="pseyxtn16v3g">
                <h2 className="text-xl font-serif font-bold text-navy-900 mb-2" data-id="element-406" data-mp="psx3ddjukfmg">How we develop our perspective</h2>
                <p className="text-sm text-gray-700 leading-relaxed" data-id="element-407" data-mp="uri6bkqhhdel">Our articles combine consultant experience, anonymized market observations, and publicly available research. Where an article references TalentBridge activity or a client example, identifying details are withheld unless permission has been provided. Editorial viewpoints are reviewed by the named consultant before publication.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter — editorial sign-up */}
      <section className="py-20 bg-white" data-id="element-408" data-mp="n9k0prbfamgn">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-409" data-mp="igh3983iysv6">
          <div className="bg-navy-900 rounded-3xl p-10 md:p-16 relative overflow-hidden" data-id="element-410" data-mp="iewbtrcibq1j">
            {/* Decorative accents */}
            <div className="absolute top-0 right-0 w-72 h-72 bg-gold-500/10 rounded-full translate-x-1/3 -translate-y-1/3 blur-3xl" data-id="element-411" data-mp="r9kgpge675pt" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-navy-700/40 rounded-full -translate-x-1/3 translate-y-1/3 blur-3xl" data-id="element-412" data-mp="165tbo5r12wwf" />

            <div className="relative grid grid-cols-1 md:grid-cols-5 gap-10 items-center" data-id="element-413" data-mp="37xojgaa5yh9d">
              <div className="md:col-span-3" data-id="element-414" data-mp="37zj3znhiw7cx">
                <div className="flex items-center gap-3 mb-4" data-id="element-415" data-mp="32zn1jsoh4nzh">
                  <Mail className="h-5 w-5 text-gold-500" data-id="element-416" data-mp="3smquppk5bvg8" />
                  <span className="text-gold-500 font-semibold tracking-[0.2em] uppercase text-xs" data-id="element-417" data-mp="3sobq1heabif5">
                    The Weekly Bridge
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4 leading-tight" data-id="element-418" data-mp="5h9mn17fqzon">
                  Get career intelligence in your inbox every Tuesday.
                </h2>
                <p className="text-gray-300 leading-relaxed" data-id="element-419" data-mp="fffl18u19nor">
                  Receive occasional executive-search intelligence on hiring trends, leadership markets, and what our consultants are seeing on the ground.
                </p>
              </div>

              <div className="md:col-span-2" data-id="element-420" data-mp="t7cat688blhz">
                <div className="space-y-3" data-id="element-421" data-mp="t6apm8xi1imj">
                  <Button variant="gold" fullWidth href="mailto:contact@talentbridge.blog?subject=Weekly%20Bridge%20subscription%20request" data-id="element-422" data-mp="y6oy1s30iul3">
                    Request The Weekly Bridge
                  </Button>
                  <p className="text-xs text-gray-400 text-center leading-relaxed" data-id="element-423" data-mp="2nlhj8csge4vf">
                    Opens a verified TalentBridge email channel. We do not sell your information. See our{' '}
                    <a href="/privacy" className="underline hover:text-gold-500 transition-colors" data-id="element-424" data-mp="2drhcy7lfqtl4">privacy policy</a>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}